import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Filter, X } from "lucide-react";
import { FilterConfig } from "./types";

interface TableFilterDialogProps {
  filters: FilterConfig[];
  activeFilters: Record<string, string>;
  onApply: (filters: Record<string, string>) => void;
  onClear: () => void;
}

/**
 * Dialog de filtros da tabela.
 * Renderiza inputs dinamicamente baseado na configuração de filtros.
 */
export function TableFilterDialog({
  filters,
  activeFilters,
  onApply,
  onClear,
}: TableFilterDialogProps) {
  const [open, setOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState<Record<string, string>>(activeFilters);

  const handleOpen = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      setLocalFilters(activeFilters);
    }
  };

  const handleApply = () => {
    Object.entries(localFilters).forEach(([key, value]) => {
      onApply({ ...activeFilters, [key]: value });
    });
    setOpen(false);
  };

  const handleClear = () => {
    setLocalFilters({});
    onClear();
    setOpen(false);
  };

  const activeFilterCount = Object.values(activeFilters).filter(Boolean).length;

  return (
    <Dialog open={open} onOpenChange={handleOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="bg-[#15203c] border-[#5d5d5d] text-white hover:bg-[#7846ff]/20 font-normal text-xs"
          aria-label="Abrir filtros"
        >
          <span className="flex gap-4">
            Filtro
            <Filter className="w-4 h-4" />
          </span>
          {activeFilterCount > 0 && (
            <span className="ml-2 bg-[#7846ff] text-white text-xs rounded-full px-2 py-0.5">
              {activeFilterCount}
            </span>
          )}
        </Button>
      </DialogTrigger>

      <DialogContent className="bg-[#0d1425] border-[#5d5d5d] text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white">Filtros</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {filters.map((filter) => (
            <div key={filter.key} className="space-y-2">
              <label className="text-sm text-gray-300">{filter.label}</label>

              {filter.type === "select" && filter.options && (
                <Select
                  value={localFilters[filter.key] || ""}
                  onValueChange={(value) =>
                    setLocalFilters((prev) => ({ ...prev, [filter.key]: value }))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder={filter.placeholder || "Selecione..."} />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {filter.type === "text" && (
                <Input
                  placeholder={filter.placeholder}
                  value={localFilters[filter.key] || ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))
                  }
                />
              )}

              {filter.type === "date" && (
                <Input
                  type="date"
                  value={localFilters[filter.key] || ""}
                  onChange={(e) =>
                    setLocalFilters((prev) => ({ ...prev, [filter.key]: e.target.value }))
                  }
                  className="text-white [color-scheme:dark]"
                />
              )}
            </div>
          ))}
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClear}
            className="bg-transparent border-[#5d5d5d] text-white hover:bg-gray-700"
          >
            <X className="w-4 h-4 mr-2" />
            Limpar
          </Button>
          <Button
            onClick={handleApply}
            className="bg-[#7846ff] text-white hover:bg-[#6835e6]"
          >
            Aplicar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
